import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import api from "api/axios";
import { AgentACLResponse, ACLEntry, Permission } from "types";
import Loader from "components/Loader";

interface ACLContextType {
  aclRoles: ACLEntry[];
  aclUsers: ACLEntry[];
  permissions: Permission[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ACLContext = createContext<ACLContextType | undefined>(undefined);

export const useACL = () => {
  const context = useContext(ACLContext);
  if (!context) {
    throw new Error("useACL must be used within an ACLProvider");
  }
  return context;
};

interface ACLProviderProps {
  children: ReactNode;
}

export const ACLProvider = ({ children }: ACLProviderProps) => {
  const { agentInfo } = useAgentInfo();
  const [aclRoles, setAclRoles] = useState<ACLEntry[]>([]);
  const [aclUsers, setAclUsers] = useState<ACLEntry[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchACL = useCallback(
    async (signal: AbortSignal) => {
      if (!agentInfo?.id) return;

      setLoading(true);
      setError(null);

      const maxRetries = 3;
      const retryDelay = 1000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await api.get<AgentACLResponse>(
            `/agents/${agentInfo.id}/acl`,
            { signal }
          );
          const { entries, permissions: perms } = response.data;

          const roles =
            entries.find((entry) => entry.subject_type === "ROLE")
              ?.acl_entries || [];
          const users =
            entries.find((entry) => entry.subject_type === "USER")
              ?.acl_entries || [];

          setAclRoles(roles);
          setAclUsers(users);
          setPermissions(perms);
          return; // Success
        } catch (err: any) {
          if (signal.aborted || err.name === "CanceledError") {
            return; // Aborted
          }

          if (err.response?.status === 500 && attempt < maxRetries) {
            // Retry on 500
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          } else {
            // Final failure
            setError("Failed to fetch ACL data. Please try again.");
            console.error("Error fetching ACL data:", err);
            return;
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [agentInfo?.id]
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchACL(abortController.signal);

    return () => abortController.abort();
  }, [fetchACL]);

  const handleRetry = async () => {
    const abortController = new AbortController();
    await fetchACL(abortController.signal);
  };

  const value = {
    aclRoles,
    aclUsers,
    permissions,
    loading,
    error,
    refetch: handleRetry,
  };

  if (loading) {
    return (
      <div className="h-[30vh] w-full  ">
        <Loader
          txt="Loading agent's ACL..."
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[30vh] w-full  ">
        <div className="absolute left-1/2 top-1/2 mt-[38px] flex -translate-x-1/2 -translate-y-1/2  flex-col items-center justify-center gap-4">
          <p className="text-lg font-medium text-red-500">{error}</p>
          <button
            onClick={handleRetry}
            className="rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <ACLContext.Provider value={value}>{children}</ACLContext.Provider>;
};
